import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { TierIiReportKind } from "generated-graphql/graphql";
import { matchPath, useLocation } from "react-router-dom";

import { SCOUT_ACCESS_PACKAGES, type ScoutAccessPackage } from "../scoutAccess";

// Slim projections used only by the Scout popover context chip. Kept separate
// from the page-owned queries so we don't accidentally pull heavy fragments.
const GET_FACILITY_NAME_FOR_SCOUT_POPOVER_CONTEXT = gql(`
  query GetFacilityNameForScoutPopoverContext($id: ID!) {
    facility(id: $id) {
      id
      name
    }
  }
`);

const GET_CHEMICAL_NAME_FOR_SCOUT_POPOVER_CONTEXT = gql(`
  query GetChemicalNameForScoutPopoverContext($chemicalId: ID!) {
    chemicalWithAssociatedData(chemicalId: $chemicalId) {
      chemical {
        id
        name
      }
    }
  }
`);

const GET_TIER_II_REPORT_LABEL_FOR_SCOUT_POPOVER_CONTEXT = gql(`
  query GetTierIIReportLabelForScoutPopoverContext($reportId: ID!) {
    tierIIReport(reportId: $reportId) {
      id
      reportingYear
      reportKind
      facility {
        id
        name
      }
    }
  }
`);

type FacilityScopedRoute = {
  pattern: string;
  format: (facilityName?: string) => string;
};

// Routes where a facilityId is the primary entity in the URL. For Scout Pro
// the label bakes in the resolved facility name behind a `Category: Value`
// prefix; other tiers see the route-aware fallback.
const FACILITY_SCOPED_ROUTES: ReadonlyArray<FacilityScopedRoute> = [
  {
    pattern: "/o/:tenantId/facilities/:facilityId/*",
    format: (name) => (name ? `Facility: ${name}` : "Facility page"),
  },
  {
    pattern: "/o/:tenantId/waste/facilities/:facilityId/*",
    format: (name) => (name ? `Facility: ${name}` : "Facility page"),
  },
  {
    pattern: "/o/:tenantId/chemicals/inventory/:facilityId/*",
    format: (name) => (name ? `Inventory: ${name}` : "Facility inventory"),
  },
  {
    pattern: "/o/:tenantId/chemicals/contacts/:facilityId",
    format: (name) => (name ? `Contacts: ${name}` : "Facility contacts"),
  },
  {
    pattern: "/o/:tenantId/chemicals/documents/:facilityId",
    format: (name) => (name ? `Documents: ${name}` : "Facility documents"),
  },
  {
    pattern: "/o/:tenantId/waste/contacts/:facilityId",
    format: (name) => (name ? `Contacts: ${name}` : "Facility contacts"),
  },
];

const CHEMICAL_DETAIL_ROUTE = "/o/:tenantId/chemicals/catalog/chemicals/:chemicalId";
const TIER_II_REPORT_ROUTE = "/o/:tenantId/chemicals/reports/:reportId/*";

// Rendered when nothing more specific matches — e.g. a route we haven't
// enumerated in `PAGE_LABELS`, or the popover being opened outside a tenant.
export const DEFAULT_SCOUT_CONTEXT_PAGE_LABEL = "This page";

// The chip's Pro label already carries a `Report:` category prefix, so each
// kind renders as just its name.
export function formatTierIIReportKind(kind: TierIiReportKind): string {
  switch (kind) {
    case TierIiReportKind.Annual:
      return "Annual";
    case TierIiReportKind.Initial:
      return "Initial";
    case TierIiReportKind.Revision:
      return "Revision";
    case TierIiReportKind.Update:
      return "Update";
    case TierIiReportKind.NewlyNotReporting:
      return "Newly Not Reporting";
  }
}

// Ordered most-specific → least-specific. `matchPath` is called in this order
// and the first hit wins, so a parent pattern like `chemicals/*` sits after
// its children (`chemicals/reports/:reportId/*` etc.).
const PAGE_LABELS: ReadonlyArray<{ pattern: string; label: string }> = [
  // Insights (detail routes before their list roots)
  { pattern: "/o/:tenantId/insights/rcra-info/:handlerId", label: "RCRA-Info handler" },
  { pattern: "/o/:tenantId/insights/echo/:registryId", label: "ECHO registry" },
  { pattern: "/o/:tenantId/insights/npdes/:npdesId", label: "NPDES site" },
  { pattern: "/o/:tenantId/insights/violations", label: "Violations" },
  { pattern: "/o/:tenantId/insights/overview", label: "Insights overview" },
  { pattern: "/o/:tenantId/insights/benchmark", label: "Benchmark" },
  { pattern: "/o/:tenantId/insights/echo", label: "ECHO insights" },
  { pattern: "/o/:tenantId/insights/rcra-info", label: "RCRA-Info insights" },
  { pattern: "/o/:tenantId/insights/npdes", label: "NPDES insights" },
  { pattern: "/o/:tenantId/insights/*", label: "Insights" },

  // Chemicals (facility-scoped routes are handled separately above)
  { pattern: "/o/:tenantId/chemicals/reports/:reportId/*", label: "Tier II report" },
  { pattern: "/o/:tenantId/chemicals/reports", label: "Reports" },
  { pattern: "/o/:tenantId/chemicals/catalog/*", label: "Chemical catalog" },
  { pattern: "/o/:tenantId/chemicals/inventory", label: "Chemical inventory" },
  { pattern: "/o/:tenantId/chemicals/contacts", label: "Chemicals contacts" },
  { pattern: "/o/:tenantId/chemicals/documents", label: "Chemicals documents" },
  { pattern: "/o/:tenantId/chemicals/products/*", label: "Products" },
  { pattern: "/o/:tenantId/chemicals/overview", label: "Chemicals overview" },
  { pattern: "/o/:tenantId/chemicals/*", label: "Chemicals" },

  // Waste (facility-scoped routes are handled separately above)
  {
    pattern: "/o/:tenantId/waste/submissions/:submissionId/stepper/*",
    label: "Waste submission",
  },
  { pattern: "/o/:tenantId/waste/submissions/:submissionId/edit", label: "Waste submission" },
  { pattern: "/o/:tenantId/waste/submissions/create", label: "New waste submission" },
  { pattern: "/o/:tenantId/waste/submissions/:submissionId", label: "Waste submission" },
  { pattern: "/o/:tenantId/waste/submissions", label: "Waste submissions" },
  { pattern: "/o/:tenantId/waste/facilities", label: "Waste facilities" },
  { pattern: "/o/:tenantId/waste/streams/*", label: "Waste streams" },
  { pattern: "/o/:tenantId/waste/manifests/*", label: "Manifests" },
  { pattern: "/o/:tenantId/waste/manifest-line-items/*", label: "Manifest line items" },
  { pattern: "/o/:tenantId/waste/contacts", label: "Waste contacts" },
  { pattern: "/o/:tenantId/waste/overview", label: "Waste overview" },
  { pattern: "/o/:tenantId/waste/*", label: "Waste" },

  // Tenant-level sections
  { pattern: "/o/:tenantId/facilities", label: "Facilities" },
  { pattern: "/o/:tenantId/contacts", label: "Contacts" },
  { pattern: "/o/:tenantId/documents/*", label: "Documents" },
  { pattern: "/o/:tenantId/credentials/*", label: "Credentials" },
  { pattern: "/o/:tenantId/tasks/*", label: "Tasks" },
  { pattern: "/o/:tenantId/account/users", label: "Users" },
  { pattern: "/o/:tenantId/organizations", label: "Organizations" },
  { pattern: "/o/:tenantId", label: "Home" },
];

export function usePopoverContextLabel(scoutAccessPackage: ScoutAccessPackage | null): string {
  const { pathname } = useLocation();
  const isPro = scoutAccessPackage === SCOUT_ACCESS_PACKAGES.PRO;

  const facilityScoped = matchFacilityScopedRoute(pathname);
  const chemicalId = matchPath(CHEMICAL_DETAIL_ROUTE, pathname)?.params.chemicalId;
  const reportId = matchPath(TIER_II_REPORT_ROUTE, pathname)?.params.reportId;

  const { data: facilityData } = useQuery(GET_FACILITY_NAME_FOR_SCOUT_POPOVER_CONTEXT, {
    variables: { id: facilityScoped?.facilityId ?? "" },
    skip: !isPro || !facilityScoped?.facilityId,
  });
  const { data: chemicalData } = useQuery(GET_CHEMICAL_NAME_FOR_SCOUT_POPOVER_CONTEXT, {
    variables: { chemicalId: chemicalId ?? "" },
    skip: !isPro || !chemicalId,
  });
  const { data: reportData } = useQuery(GET_TIER_II_REPORT_LABEL_FOR_SCOUT_POPOVER_CONTEXT, {
    variables: { reportId: reportId ?? "" },
    skip: !isPro || !reportId,
  });

  if (facilityScoped) {
    const name = isPro ? (facilityData?.facility?.name ?? undefined) : undefined;
    return facilityScoped.format(name);
  }
  if (chemicalId) {
    const name = isPro
      ? (chemicalData?.chemicalWithAssociatedData?.chemical?.name ?? undefined)
      : undefined;
    return name ? `Chemical: ${name}` : "Chemical details";
  }
  if (reportId) {
    if (isPro) {
      const report = reportData?.tierIIReport;
      const facilityName = report?.facility?.name;
      if (facilityName && report?.reportingYear && report?.reportKind) {
        return `Report: ${facilityName} ${report.reportingYear} ${formatTierIIReportKind(report.reportKind)}`;
      }
    }
    return "Tier II report";
  }

  return resolvePageLabelForRoute(pathname);
}

export function matchFacilityScopedRoute(
  pathname: string
): { facilityId: string; format: (name?: string) => string } | null {
  for (const { pattern, format } of FACILITY_SCOPED_ROUTES) {
    const match = matchPath(pattern, pathname);
    const facilityId = match?.params.facilityId;
    if (facilityId) {
      return { facilityId, format };
    }
  }
  return null;
}

export function resolvePageLabelForRoute(pathname: string): string {
  for (const { pattern, label } of PAGE_LABELS) {
    if (matchPath(pattern, pathname)) {
      return label;
    }
  }
  return DEFAULT_SCOUT_CONTEXT_PAGE_LABEL;
}
